import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Menu, X, MapPin } from 'lucide-react';
import SearchDropdown from './SearchDropdown';
import destinationsData from '../data/destinations.json';
import './Header.css';

const Header = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [isHidden, setIsHidden] = useState(false);
  const searchContainerRef = useRef(null);
  const headerRef = useRef(null);
  const navOriginalTopRef = useRef(null);

  // Get all destinations from JSON
  const allDestinations = [
    ...Object.keys(destinationsData.destinations),
    ...destinationsData.navigationDestinations.map(dest => dest.name).filter(name => 
      !Object.keys(destinationsData.destinations).includes(name)
    ),
    // Add additional popular destinations
    'Singapore', 'Japan', 'Sri Lanka', 'Nepal', 'Indonesia', 'Turkey', 'Greece', 'France', 'Germany', 'Italy', 'Spain', 'Britain'
  ];

  // Filter suggestions based on search term
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    if (value.length > 0) {
      const filtered = allDestinations
        .filter(dest => dest.toLowerCase().includes(value.toLowerCase()))
        .slice(0, 8); // Show top 8 suggestions
      setFilteredSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion);
    setShowSuggestions(false);
  };

  // Handle input focus
  const handleInputFocus = () => {
    setIsSearchDropdownOpen(true);
    if (searchTerm.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  // Store the original position of destination-nav on mount
  useEffect(() => {
    const destinationNav = document.querySelector('.destination-nav');
    if (destinationNav && !navOriginalTopRef.current) {
      const rect = destinationNav.getBoundingClientRect();
      navOriginalTopRef.current = rect.top + window.scrollY;
    }
  }, []);

  // Detect when destination-nav becomes fixed and hide header
  useEffect(() => {
    const handleScroll = () => {
      if (navOriginalTopRef.current) {
        const scrollPosition = window.scrollY;
        // Hide header when we scroll past the original nav position
        // Show header when we scroll back above the original nav position
        const shouldHide = scrollPosition >= navOriginalTopRef.current;
        setIsHidden(shouldHide);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on mount
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return (
    <header className={`header ${isHidden ? 'hidden' : ''}`} ref={headerRef}>
      <div className="container">
        <div className="header-content">
          {/* Logo */}
          <div 
            className="logo" 
            onClick={handleLogoClick}
            style={{ cursor: 'pointer' }}
          >
            <img src="/logo.png" alt="Emrys Logo" className="logo-img" />
            <img src="/emrys-text.png" alt="EMRYS" className="logo-text" />
          </div>

          {/* Centered Search Bar */}
          <div className="centered-search" ref={searchContainerRef}>
            <div className="search-input-container">
              <Search size={18} className="search-icon" />
              <input
                type="text"
                placeholder="Search for destinations, tours, experiences..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={handleInputFocus}
                onClick={handleInputFocus}
                onBlur={() => {
                  // Delay hiding suggestions to allow clicking on them
                  setTimeout(() => setShowSuggestions(false), 200);
                }}
              />
            </div>

            {/* Search Suggestions Dropdown */}
            {showSuggestions && filteredSuggestions.length > 0 && (
              <div className="search-suggestions">
                <div className="suggestions-header">
                  <MapPin size={16} />
                  <span>Popular Destinations</span>
                </div>
                {filteredSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="suggestion-item"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <MapPin size={14} />
                    <span>{suggestion}</span>
                  </div>
                ))}
              </div>
            )}

            <SearchDropdown 
              isOpen={isSearchDropdownOpen} 
              onClose={() => setIsSearchDropdownOpen(false)}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
            />
          </div>

          {/* Actions */}
          <div className="header-actions">
            <button className="btn btn-primary">Login</button>
            <button className="btn btn-secondary">Sign Up</button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="mobile-menu-btn"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="mobile-nav">
            <ul className="mobile-nav-links">
              <li><button className="btn btn-primary">Login</button></li>
              <li><button className="btn btn-secondary">Sign Up</button></li>
            </ul>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
